import html2pdf from "html2pdf.js";
import axios from "axios";
import "jspdf-autotable";
import logo from "../../assets/Visteon-Logo.png";

export default async function downloadSLACertificate(
  oem,
  product,
  environment,
  apiType,
  duration,
  apiUrl,
  accessToken,
  oemDescription,
  environmentDescription,
  productDescription
) {
  const filters = `Environment: ${environment}, API Type: ${apiType}, OEM: ${oem}, Product: ${product}`;
  const number = parseInt(duration);
  const formattedDuration = formatDurationText(duration);
  const data = await getMainpageData(
    oem,
    product,
    environment,
    apiType,
    duration,
    apiUrl,
    accessToken
  );
  if (!data.overallResults || data.overallResults.length === 0) {
    throw new Error("No endpoint data available for the selected filters and duration. Please try different filter combinations or check if endpoints are configured for this selection.");
  }
  const currentDate = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const formattedDate = formatter.format(currentDate);
  const overallAvailability =
    parseFloat(data.overallAvailability).toFixed(2) + "%";
  if (typeof html2pdf === "undefined") {
    await import("html2pdf.js");
  }
  const element = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>SLA Certificate</title>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap');
        
        body {
            font-family: 'Poppins', Arial, sans-serif;
            background-color: #f4f4f4;
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
        }
        .container {
            background-color: #fff;
            border-radius: 12px;
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
            max-width: 650px;
            text-align: center;
            border: 1px solid #ccc;
            overflow: hidden;
        }
        .header {
            background-color: #003366;
            color: #ffffff;
            display: flex;
            flex-direction: column;
            align-items:center;
            padding: 25px;
            text-align: center;
            box-shadow: inset 0 -4px 8px rgba(0, 0, 0, 0.3); /* Inner shadow */
        }
        .header img {
            max-height: 90px;
            margin-bottom: 10px;
        }
        .header h1 {
            margin: 0;
            font-size: 28px;
            font-weight: 700;
        }
        .content {
            padding: 30px;
        }
        .content h2 {
            color: #003366;
            font-size: 34px;
            margin: 10px 0;
        }
        .content p {
            font-size: 18px;
            line-height: 1.7;
            color: #555555;
            margin: 15px 0;
        }
        .highlight {
            font-size: 60px;
            font-weight: 700;
            color: #003366;
            margin: 10px 0;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .highlight .icon {
            color: #28a745;
            font-size: 30px;
            margin-right: 10px;
        }
        .highlight span {
            margin-bottom: 0;
        }
        .uptime-label {
            font-size: 22px;
            color: #003366;
            margin-top: 5px;
        }
        .link {
            margin-top: 20px;
            font-size: 18px;
            color: #003366;
            font-weight: 600;
        }
        .link a {
            color: #003366;
            text-decoration: none;
        }
        .footer {
            background-color: #f4f4f4;
            padding: 20px;
            font-size: 14px;
            color: #777777;
            display: flex;
            flex-direction: column;
            align-items: center;
        }
        .footer p {
            margin: 5px 0;
        }
        .footer img {
            max-height: 20px;
            margin-left: 5px;
            vertical-align: middle;
        }
            .logo-container {
      display: flex;
      justify-content: center;
      align-items: center;
    }
        .date {
            font-size: 12px;
            color: #555555;
        }
        .email {
            margin-top: 5px;
            font-size: 14px;
            color: #003366;
            font-weight: 600;
        }
    </style>
</head>
<body>
    <div class="container" style="box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);">
        <div class="header">
            <img src="logo-cloud.png" alt="Visteon Logo">
            <h1>Service Level Objective Certificate</h1>
        </div>
        <div class="content">
            <p>This certifies that the ${environmentDescription} environment for <strong>${oemDescription}</strong> has consistently maintained a high level of availability for product ${productDescription}.</p>
            <div class="highlight">
                <span class="icon">&#x25B2;</span>
                <span>${overallAvailability}</span>
            </div>
            <div class="uptime-label">Uptime</div>
            <p><strong>Monitoring Period:</strong>${formattedDuration}</p>
            <p>This demonstrates the reliability and stability of our services, ensuring minimal downtime and optimal performance throughout the monitoring period.</p>
            <div class="link">
                For more details, visit: <a href="https://cloudlens.visteoncloud.com" target="_blank">cloudlens.visteoncloud.com</a>
            </div>
        </div>
        <div class="footer">
          
            <p>Issued by:</p>
            <img src="${logo}" alt="Company Logo" style="margin-bottom: 20px;">
              <p class="date">Date: <strong>${formattedDate}</strong></p>
            <p class="email">Contact: <a href="mailto:cloudlens@visteon.com">cloudlens@visteon.com</a></p>
        </div>
    </div>
</body>
</html>
`;
  const opt = {
    margin: [20, 20],
    filename: "SLA_Certificate.pdf",
    image: { type: "jpeg", quality: 1 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      logging: false,
    },
    jsPDF: {
      unit: "mm",
      format: "a4",
      orientation: "portrait",
    },
    enableLinks: true,
    pagebreak: { mode: "avoid-all" },
    output: "save",
    putTotalPages: true,
    html2pdf: {
      textContent: "preserve",
      imageQuality: 1,
      enableTextLayer: true,
    },
  };

  // Generate and download PDF
  await html2pdf()
    .from(element)
    .set(opt)
    .toPdf()
    .get("pdf")
    .then((pdf) => {
      // Ensure PDF text layer is enabled
      pdf.internal.events.subscribe("putFont", function () {
        this.font.subset = false;
      });
      return pdf;
    })
    .save();
}
const getMainpageData = async (
  oem,
  product,
  environment,
  apiType,
  duration,
  apiUrl,
  accessToken
) => {
  const query = `
    query GetMainEndpointPageDetails($duration: String!, $apiType: String!, $environment: String!, $oem: String!, $product: String!) {
      getMainEndpointPageDetails(duration: $duration, filters: {ApiType: $apiType, Environment: $environment, OemName: $oem, ProductName: $product}) {
        graphresultsArray {
          endpointName
           averagePerDayData{
            avgAvailability
            timeInterval
          }
        }
        overallAvailability
        overallResults {
          availabilityPercentage
          downRequests
          endpoint
          latestStatus
          latestTimestamp
          totalRequests
        }
      }
    }
  `;
  const response = await axios.post(
    apiUrl,
    {
      query,
      variables: {
        duration,
        environment,
        apiType,
        oem,
        product,
      },
    },
    {
      headers: {
        Authorization: accessToken,
      },
    }
  );

  return response.data.data.getMainEndpointPageDetails;
};

function formatDuration(duration) {
  const match = duration.match(/(\d+)(months?|days?)/i);

  if (!match) {
    console.error(
      'Invalid duration format. Please provide a number followed by "month" or "day".'
    );
    return null;
  }

  const number = parseInt(match[1]);
  const unit = match[2].toLowerCase();

  if (isNaN(number)) {
    console.error("Invalid number in duration.");
    return null;
  }

  let formattedUnit;
  if (unit === "months" || unit === "month") {
    formattedUnit = number === 1 ? "month" : "months";
  } else if (unit === "days") {
    formattedUnit = number === 1 ? "day" : "days";
  } else {
    console.error("Invalid unit in duration.");
    return null;
  }

  return number + " " + formattedUnit;
}
function formatDurationText(duration) {
  const monthYearPattern = /^(\d{2})-(\d{4})$/;
  const match = duration.match(monthYearPattern);
  if (match) {
    const [, month, year] = match;
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthIndex = parseInt(month) - 1;
    return `In ${monthNames[monthIndex]} ${year}`;
  }
  const durationMap = {
    '7days': 'In the last 7 days',
    '1Month': 'In the last month',
    '3months': 'In the last 3 months',
  };
  return durationMap[duration] || `In the last ${duration}`;
}